import React, {
  useEffect,
} from 'react';

import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';

import { TextField } from '@waglabs/textfield';
import { Button } from '@waglabs/button-next';
import {
  ChevronLeftIcon,
} from '@waglabs/icons';
import { Box } from '@waglabs/box-next';
import { Flex } from '@waglabs/flex-next';
import { Typography } from '@waglabs/typography';

import type { UseStepperReturnType } from '../../../../../../hooks/useStepper';
import { usePostAuthenticationSession } from '../../../../../../hooks/api/usePostAuthenticationSession';

import { getFormikFieldHelper } from '../../../../../../utils/getFormikHelper';
import { pipe } from '../../../../../../utils/pipe';
import { ownerSlice } from '../../../../../../slices/owners';
import { authSlice } from '../../../../../../slices/auth';

import {
  Auth,
  DATA_TEST_IDS,
} from '../../../../../../constants';
import { EnvelopeOutlinedIcon } from '../../../../../../icons/EnvelopeOutlinedIcon';

type LoginByEmailFormStep1Props = UseStepperReturnType & {};

const FormField = Object.freeze({
  Email: 'email',
});

export const LoginByEmailFormStep1 = ({
  handleNext,
  handleBack,
  setMeta,
}: LoginByEmailFormStep1Props) => {
  const {
    execute: createAuthenticationSession,
    isSuccess,
    error: postAuthenticationSessionError,
    errorMessage: postAuthenticationSessionErrorMessage,
    reset,
  } = usePostAuthenticationSession();

  const socialAuthProviderToLink = useSelector(authSlice.selectors.getSocialAuthProviderToLink);
  const socialAuthEmail = useSelector(ownerSlice.selectors.getSocialEmail);

  const getDefaultEmail = () => {
    if (socialAuthProviderToLink && socialAuthEmail) {
      return socialAuthEmail;
    }
    return '';
  }

  const schema = Yup.object().shape({
    [FormField.Email]: Yup.string().email().required().default(`${getDefaultEmail()}`)
      .label('Email'),
  });

  const {
    errors,
    isValid,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    handleChange: handleFormikChange,
    handleBlur,
    setSubmitting,
  } = useFormik({
    isInitialValid: !!(socialAuthEmail),
    initialValues: schema.cast(),
    validationSchema: schema,
    onSubmit: (formValues) => {
      setMeta({
        [FormField.Email]: formValues[FormField.Email],
      });
      createAuthenticationSession({
        userRoleIdentifier: formValues[FormField.Email],
        userRoleIdentificationMethod: Auth.AllowedMethods.Email,
        userRoleType: Auth.RoleType.Owner,
      });
    },
  });

  // Removes state error when the user update field text
  const handleChange = pipe(
    handleFormikChange,
    () => {
      if (!postAuthenticationSessionError) {
        return;
      }
      reset();
    },
  );

  const formikFieldHelper = getFormikFieldHelper({
    errors,
    touched,
  });

  const fieldError = (
    postAuthenticationSessionError || formikFieldHelper(FormField.Email).hasError
  );
  const fieldHelperText = (
    postAuthenticationSessionErrorMessage || formikFieldHelper(FormField.Email).errorMessage
  );

  useEffect(() => {
    // Reset on mount
    reset();
  }, [
    reset,
  ]);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    // reset it here knowing that it is a successful event; Allows the user to go back
    // Otherwise, the user will be stuck on the next page and unable to go back
    // because this effect will keep on running
    reset();
    handleNext();
  }, [
    isSuccess,
    handleNext,
    reset,
  ]);

  useEffect(() => {
    if (!postAuthenticationSessionError) {
      return;
    }
    setSubmitting(false);
  }, [
    postAuthenticationSessionError,
    setSubmitting,
  ]);

  return (
    <>
      <Flex
        height="24px"
        alignItems="center"
        onClick={() => {
          handleBack();
        }}
        hover
      >
        <ChevronLeftIcon
          color="wagGreen"
          cursor="pointer"
        />
        <Typography
          m={0}
          color="wagGreen"
          hover
        >
          Back
        </Typography>
      </Flex>
      <Box
        // polymorphism technique
        as="form"
        onSubmit={handleSubmit}
        py="24px"
      >
        <TextField
          label="Email address"
          type="email"
          name={FormField.Email}
          inputProps={{
            fontFamily: 'muli',
            startAdornment: () => (
              <EnvelopeOutlinedIcon />
            ),
          }}
          containerProps={{
            backgroundColor: 'white',
            // Due to nature of this page having a grey background,
            // we need to update the backgroundColor. Having said that,
            // the Textfield component thought the user is manually handling it
            // that's why we have to manually set the borderColor
            // TODO - Update this on @waglabs/textfield
            borderColor: fieldError ? 'red' : 'white',
          }}
          error={fieldError}
          helperText={fieldHelperText}
          value={values[FormField.Email]}
          onChange={handleChange}
          onBlur={handleBlur}
          data-testId={DATA_TEST_IDS.LOGIN.loginByEmailForm.Email}
          fullWidth
        />
        <Button
          variant="contained"
          type="submit"
          disabled={!isValid || isSubmitting}
          mt="12px"
          mb="24px"
          data-testId={DATA_TEST_IDS.LOGIN.loginByEmailForm.ContinueBtn}
        >
          Continue
        </Button>
      </Box>
    </>
  );
};
